import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import messages from '@configurator-messages'
import {
  Theme,
  Grid,
  Link,
  Box,
  Divider,
  DialogContent,
  Button,
  DialogTitle,
  Dialog,
  IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {
  customOligoRoute,
  cartRoute,
  milliplexRoute,
  useRouter,
  CartView,
  CartType,
} from '@src/routes'
import {
  productData,
  Products,
  customCrisprProductData,
} from '@src/routes/Configurators/utils/productData'
import {
  GenomicProduct,
  GenomicsProperties,
} from '@src/routes/Configurators/products/Genomics/enum'
import { ProductEnum } from '@src/routes/Configurators/utils/MilliplexEnum'
import { ActiveLinkType } from '@src/routes/SemiConfigurators/products/crisprgrna/Enum'
import { useUserSession } from '@utils/useUserSession'
import { InvalidKeys } from '@src/routes/Configurators/utils/constants'
import clsx from 'clsx'
import { useCurrentUser } from '@utils/useCurrentUser'
import getConfig from 'next/config'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import globalStyles from '@src/routes/Configurators/utils/styles'

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiDialog-paperWidthMd': {
      width: '100%',
      maxWidth: theme.spacing(210),
      padding: theme.spacing(8),
    },
  },
  textAlign: {
    padding: theme.spacing(6.5, 0, 0, 3),
    color: theme.palette.primary.main,
  },
  pos: {
    marginBottom: theme.spacing(3),
  },
  nextButton: {
    marginLeft: theme.spacing(4),
    padding: theme.spacing(2, 6),
  },

  divMar: {
    margin: theme.spacing(6, 0),
  },
  title: {
    textTransform: 'uppercase',
    padding: 0,
    '& h2': {
      position: 'relative',
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  productContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(4),
    background: theme.palette.background.grey,
    gap: theme.spacing(2),
    maxWidth: '30%',
  },

  closeButton: {
    position: 'absolute',
    right: theme.spacing(0),
    top: theme.spacing(0),
    color: theme.palette.common.black,
    padding: 0,
    '& svg': {
      fontSize: theme.typography.pxToRem(32),
    },
    '&.MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
    },
  },
  actionButtons: {
    flexDirection: 'row-reverse',
    alignItems: 'baseline',
  },
  dialogContent: {
    padding: theme.spacing(6, 0, 0, 0),
  },
  productTitle: {
    fontWeight: 900,
    flexBasis: 'auto',
  },
  recommendedProducts: {
    marginTop: theme.spacing(6),
    justifyContent: 'space-between',
  },
  subTitle: {
    fontWeight: 900,
    fontSize: theme.typography.pxToRem(18),
  },
  buttonFontWeight: {
    fontWeight: 'bold',
  },
  buttonBorder: {
    border: '1px solid #0f68ae',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  warning: {
    fontWeight: 700,
    display: 'flex',
    gap: theme.spacing(2),
  },
  cartSummarySubTotalContainer: {
    padding: theme.spacing(6, 0),
    gap: theme.spacing(2),
  },
  cartSummarySubTotal: {
    display: 'flex',
    gap: theme.spacing(2),
    fontSize: theme.typography.pxToRem(14),
  },
  cartSummarySubTotalLabel: {
    fontWeight: 700,
  },
  productActionButtons: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
  },
}))

interface AddToCartItemsMTOProps {
  dialogOpen: boolean
  handleDialogClose: React.Dispatch<React.SetStateAction<boolean>>
  price: React.ReactNode
  totalItem: number
  companionProducts?: any[] | null | undefined
}

const AddToCartItemsMTO: React.FC<AddToCartItemsMTOProps> = (props) => {
  const classes = useStyles()
  const {
    publicRuntimeConfig: { featureFlags },
  } = getConfig()
  const { dialogOpen, price, totalItem, companionProducts, handleDialogClose } =
    props
  const {
    userSession: { language, country },
  } = useUserSession()
  const { hasOnlyBlueERP } = useCurrentUser()
  const router = useRouter()
  const { product = '' } = router.query
  const GlobalCSS = globalStyles()

  const companionProductData: Products[] = []
  if (companionProducts) {
    if (
      product === GenomicProduct.CustomCrispr ||
      product === ActiveLinkType.ProductType
    ) {
      companionProducts.forEach((item: string) => {
        const product: Products = {
          title: '',
          displayName: {
            id: InvalidKeys.KEY_NOT_PRESENT,
            defaultMessage: InvalidKeys.KEY_NOT_PRESENT,
          },
          description: {
            id: InvalidKeys.KEY_NOT_PRESENT,
            defaultMessage: InvalidKeys.KEY_NOT_PRESENT,
          },
          url: '',
          continueButtonUrl: '',
          productCompany: {
            id: InvalidKeys.KEY_NOT_PRESENT,
            defaultMessage: InvalidKeys.KEY_NOT_PRESENT,
          },
        }
        customCrisprProductData.forEach((obj) => {
          if (obj.title === item) {
            product.title = obj.title
            product.description = obj.description
            product.displayName = obj.displayName
            product.continueButtonUrl = obj.continueButtonUrl
            product.productCompany = obj.productCompany
            if (item === GenomicsProperties.ModifiedFNCAS9TRACR) {
              product.url = obj.url
            } else {
              product.url = `/${country}/${language}/${obj.url}`
            }
          }
        })
        if (product.title.length) {
          companionProductData.push(product)
        }
      })
    } else {
      companionProducts.forEach((item: string) => {
        const product: Products = {
          title: item,
          displayName: featureFlags.internalAndDualQuenchProbes
            ? messages.CONF_STANDARD_TUBES_PROBE_NEW
            : messages.CONF_DNA_OLIGOS_IN_TUBES_PROBE,
          description: messages.DNA_OLIGO_IN_TUBES_DESC,
          url: '',
          continueButtonUrl: '',
          productCompany: messages.CONF_COMPANY_SIGMA,
        }
        productData.forEach((obj) => {
          if (obj.title === item) {
            product.title = obj.title
            product.description = obj.description
            product.displayName = obj.displayName
            product.url = obj.url
            product.continueButtonUrl = obj.continueButtonUrl
            product.productCompany = obj.productCompany
          }
        })

        companionProductData.push(product)
      })
    }
  }

  const getContinueButtonURL = () => {
    if (product === ActiveLinkType.ProductType) {
      return '/products/molecular-biology-and-functional-genomics/gene-editing-and-functional-genomics/crispr-gene-editing'
    } else {
      const data = productData.filter((item) => item.title === product)
      return data && data.length
        ? data[0].continueButtonUrl
        : customOligoRoute.index()
    }
  }

  const renderCompanionProduct = () => {
    if (companionProductData)
      return companionProductData.map((data, index) => (
        <Grid item xs={4} key={index} className={classes.productContainer}>
          <Grid item xs={12} className={classes.productTitle}>
            <FormattedMessage
              {...data.displayName}
              values={{
                registered: (
                  <sup>
                    <FormattedMessage {...messages.CONF_TRADEMARK} />
                  </sup>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormattedMessage
              {...data.description}
              values={{
                registered: (
                  <sup>
                    <FormattedMessage {...messages.CONF_TRADEMARK} />
                  </sup>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} className={classes.productActionButtons}>
            <Button
              variant="outlined"
              className={clsx(classes.buttonFontWeight, classes.buttonBorder)}
              color="primary"
              fullWidth
              onClick={() => {
                if (data.url !== '') {
                  window.location.assign(data.url)
                }
              }}
            >
              {data.title === GenomicsProperties.ModifiedFNCAS9TRACR ? (
                <FormattedMessage {...messages.REQUEST_QUOTE} />
              ) : (
                <FormattedMessage
                  id="CONF_CONFIGURE"
                  defaultMessage="Configure"
                />
              )}
            </Button>
          </Grid>
        </Grid>
      ))
    else {
      return ''
    }
  }

  const handleClose = () => {
    handleDialogClose && handleDialogClose(false)
  }

  const onCheckout = () => {
    window.location.assign(
      hasOnlyBlueERP
        ? cartRoute[CartView.Cart](CartType.BlueCart)
        : cartRoute[CartView.Cart]()
    )
  }

  return (
    <Box>
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        aria-label=""
        open={dialogOpen}
        maxWidth="md"
        className={classes.dialog}
        id="add-to-cart-modal"
        data-testid="add-to-cart-modal"
      >
        <DialogTitle className={classes.title}>
          <FormattedMessage
            id="CONF_ADDED_TO_SHOPPIN_CART_TITLE_REDESIGN"
            defaultMessage="Your shopping cart has been updated"
          />
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Grid container>
            <Grid item xs={12} className={classes.warning}>
              <WarningRoundedIcon className={GlobalCSS.warningIcon} />
              <FormattedMessage
                id="CONF_ADDED_TO_SHOPPIN_CART_WARNING"
                defaultMessage="Custom products cannot be modified or canceled upon order placement."
              />
            </Grid>
            <Grid container className={classes.cartSummarySubTotalContainer}>
              <Grid item xs={12} className={classes.cartSummarySubTotal}>
                <Grid item className={classes.cartSummarySubTotalLabel}>
                  <FormattedMessage
                    id="CART_SUMMARY"
                    defaultMessage="Summary"
                  />
                  :
                </Grid>
                <Grid item>
                  {totalItem}&nbsp;
                  <FormattedMessage
                    id="CONF_ITEM_ADDED_TO_SHOPPIN_CART"
                    defaultMessage="item has been added to your shopping cart"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.cartSummarySubTotal}>
                <Grid item className={classes.cartSummarySubTotalLabel}>
                  <FormattedMessage
                    id="CART_SUBTOTAL"
                    defaultMessage="Subtotal"
                  />
                  :
                </Grid>
                <Grid item>
                  <label>{price}</label>
                </Grid>
              </Grid>
            </Grid>
            <Grid container className={classes.actionButtons}>
              <Button
                color="primary"
                variant="contained"
                className={classes.nextButton}
                onClick={onCheckout}
                id="checkout-button"
                data-testid="checkout-button"
              >
                <FormattedMessage {...messages.CONF_PROCEED_TO_CHECKOUT} />
              </Button>
              {product === ProductEnum.Milliplex ? (
                <div className={classes.textAlign}>
                  <Link href={milliplexRoute.index()}>
                    <h3>
                      <FormattedMessage {...messages.CONF_CONTINUE_SHOPPING} />
                    </h3>
                  </Link>
                </div>
              ) : (
                <div className={classes.textAlign}>
                  <Link href={getContinueButtonURL()}>
                    <h3>
                      <FormattedMessage {...messages.CONF_CONTINUE_SHOPPING} />
                    </h3>
                  </Link>
                </div>
              )}
            </Grid>
          </Grid>
          {companionProducts && (
            <>
              <div>
                <Divider className={classes.divMar} />
              </div>

              <Grid xs={12}>
                <div>
                  <h2 className={classes.subTitle}>
                    <FormattedMessage
                      id="CONF_CUSTOMERS_ALSO_PURCHASED"
                      defaultMessage="Customers Also Purchased"
                    />
                  </h2>
                </div>
              </Grid>
              <Grid container className={classes.recommendedProducts}>
                {renderCompanionProduct()}
              </Grid>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default AddToCartItemsMTO
